/**
 * ClientsRelinkPanel - Paramètres / outils admin.
 *
 * Scanne tous les comptes clients (role=client) et propose de les rattacher
 * automatiquement a leur lead + projet via le matching email.
 *
 * Resout le cas : "Quand le client se connecte, il voit 'aucun projet associe a votre compte'".
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientsRelinkPanel = () => {
  const [plan, setPlan] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState(null);
  const [manualUser, setManualUser] = useState(null);  // user en cours de lien manuel
  const [allProjects, setAllProjects] = useState([]);
  const [manualProjectId, setManualProjectId] = useState('');
  const [manualAsCoInv, setManualAsCoInv] = useState(false);
  const [manualLinking, setManualLinking] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/clients/projects-for-link`);
      setAllProjects(r.data || []);
    } catch { setAllProjects([]); }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/clients/relink/preview`);
      setPlan(r.data);
      const init = {};
      (r.data.items || []).filter(it => it.status === 'needs_relink').forEach((it) => {
        init[it.user_id] = true;
      });
      setSelected(init);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoadingPreview(false); }
  }, []);

  const execute = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      alert('Selectionnez au moins un compte a rattacher.');
      return;
    }
    if (!window.confirm(`Rattacher ${ids.length} compte(s) client a leur projet ?`)) return;
    setExecuting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/clients/relink/execute`, { user_ids: ids });
      setResult(r.data);
      setTimeout(() => loadPreview(), 800);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setExecuting(false); }
  };

  const toggleAll = (val) => {
    if (!plan?.items) return;
    const next = {};
    plan.items.filter(it => it.status === 'needs_relink').forEach((it) => {
      next[it.user_id] = val;
    });
    setSelected(next);
  };

  const startManualLink = async (orphanUser) => {
    setManualUser(orphanUser);
    setManualProjectId('');
    setManualAsCoInv(false);
    if (allProjects.length === 0) await loadProjects();
  };

  const submitManualLink = async () => {
    if (!manualProjectId) {
      alert('Selectionnez un projet.');
      return;
    }
    setManualLinking(true);
    try {
      await axios.post(`${API}/admin/clients/${manualUser.user_id}/manual-link`, {
        project_id: manualProjectId,
        as_co_investor: manualAsCoInv,
      });
      setManualUser(null);
      await loadPreview();
    } catch (e) {
      alert(e.response?.data?.detail || 'Echec lien manuel');
    } finally { setManualLinking(false); }
  };

  const needsRelink = (plan?.items || []).filter(it => it.status === 'needs_relink');
  const orphans = (plan?.items || []).filter(it => it.status === 'orphan');

  return (
    <div className="realease-card p-6" data-testid="clients-relink-panel">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          Rattacher les comptes clients orphelins
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Resout le cas <i>"Aucun projet associe a votre compte"</i> en matchant chaque compte client a son lead/projet via son email.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
          data-testid="clients-relink-preview-btn"
        >
          {loadingPreview ? 'Analyse...' : 'Analyser les comptes clients'}
        </button>
        {needsRelink.length > 0 && (
          <>
            <button
              type="button"
              onClick={execute}
              disabled={executing}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-[#A57F4F] text-white hover:bg-[#8d6a40] disabled:opacity-50 transition"
              data-testid="clients-relink-execute-btn"
            >
              {executing ? 'Rattachement...' : `Rattacher ${Object.values(selected).filter(Boolean).length} compte(s)`}
            </button>
            <button type="button" onClick={() => toggleAll(true)} className="text-xs text-[#13736D] hover:underline">Tout selectionner</button>
            <button type="button" onClick={() => toggleAll(false)} className="text-xs text-gray-500 hover:underline">Tout deselectionner</button>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="clients-relink-error">
          Erreur : {error}
        </div>
      )}

      {plan && (
        <>
          {needsRelink.length === 0 && orphans.length === 0 ? (
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-700" data-testid="clients-relink-empty">
              Tous les comptes clients sont correctement rattaches.
            </div>
          ) : (
            <>
              {needsRelink.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    {needsRelink.length} compte(s) a rattacher :
                  </p>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-xs" data-testid="clients-relink-list">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-2 py-2 w-6"></th>
                          <th className="px-2 py-2 text-left">Email</th>
                          <th className="px-2 py-2 text-left">Nom</th>
                          <th className="px-2 py-2 text-left">Lead cible</th>
                          <th className="px-2 py-2 text-left">Projet cible</th>
                          <th className="px-2 py-2 text-left">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {needsRelink.map((it) => (
                          <tr key={it.user_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={!!selected[it.user_id]}
                                onChange={(e) => setSelected({ ...selected, [it.user_id]: e.target.checked })}
                                data-testid={`clients-relink-check-${it.user_id}`}
                              />
                            </td>
                            <td className="px-2 py-2 font-medium text-gray-900">{it.email}</td>
                            <td className="px-2 py-2 text-gray-700">{it.name || '—'}</td>
                            <td className="px-2 py-2 font-mono text-[10px] text-gray-600">{it.target_lead_id || '—'}</td>
                            <td className="px-2 py-2 font-mono text-[10px] text-[#13736D] font-semibold">{it.target_project_id || '—'}</td>
                            <td className="px-2 py-2">
                              {it.is_co_investor ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#A57F4F]/10 text-[#A57F4F] font-semibold">CO-INV.</span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">PRINCIPAL</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {orphans.length > 0 && (
                <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="clients-relink-orphans">
                  <p className="text-sm font-semibold text-amber-800 mb-2">
                    {orphans.length} compte(s) orphelin(s) (aucun lead/projet matchant l'email) :
                  </p>
                  <ul className="text-xs space-y-1.5">
                    {orphans.map((o) => (
                      <li
                        key={o.user_id}
                        className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1.5 border border-amber-200"
                        data-testid={`clients-relink-orphan-${o.user_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-gray-800 truncate">{o.email}</p>
                          {o.name && <p className="text-[10px] text-gray-500 truncate">{o.name}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => startManualLink(o)}
                          className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] transition shrink-0"
                          data-testid={`clients-relink-manual-${o.user_id}`}
                        >
                          Lier manuellement
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-amber-600 mt-2">
                    Aucun lead avec cet email. Soit l'email du lead a une typo, soit le projet n'a pas encore ete cree. Utilisez le bouton "Lier manuellement" si vous savez a quel projet le rattacher.
                  </p>
                </div>
              )}

              {/* Dialog lien manuel */}
              {manualUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="clients-manual-link-modal">
                  <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full">
                    <h4 className="font-bold text-gray-900 mb-1">Lier manuellement</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Compte : <span className="font-mono">{manualUser.email}</span>
                    </p>
                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Projet *</label>
                    <select
                      value={manualProjectId}
                      onChange={(e) => setManualProjectId(e.target.value)}
                      className="realease-input text-sm mt-1 mb-3"
                      data-testid="clients-manual-link-project-select"
                    >
                      <option value="">— Selectionner un projet —</option>
                      {allProjects.map((p) => (
                        <option key={p.project_id} value={p.project_id}>
                          {p.name} {p.client_name ? `(${p.client_name})` : ''}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm mb-4">
                      <input
                        type="checkbox"
                        checked={manualAsCoInv}
                        onChange={(e) => setManualAsCoInv(e.target.checked)}
                        data-testid="clients-manual-link-coinv"
                      />
                      Lier en tant que co-investisseur (ne devient pas le client principal)
                    </label>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setManualUser(null)}
                        className="px-3 py-1.5 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={submitManualLink}
                        disabled={manualLinking || !manualProjectId}
                        className="px-3 py-1.5 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
                        data-testid="clients-manual-link-submit"
                      >
                        {manualLinking ? 'Liaison...' : 'Lier'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {result && (
        <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200" data-testid="clients-relink-result">
          <p className="font-semibold text-emerald-800 mb-2">
            Rattachement effectue - {result.processed} compte(s) traite(s).
          </p>
          <ul className="text-xs text-emerald-700 space-y-0.5">
            {(result.results || []).map((r) => (
              <li key={r.user_id}>
                {r.ok ? '✓' : '✗'} {r.email}{r.linked_project_id ? ` -> ${r.linked_project_id}` : ''}{r.error ? ` (${r.error})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClientsRelinkPanel;
